import { Typography } from 'components/Typography'
import { FieldWithLabel } from 'components/FieldWithLabel/FieldWithLabel'
import { Separator } from 'components/Separator/Separator'
import { RowLayout } from 'features/MyCommunity/Vendors/VendorCreation/General/VendorForm/RowLayout'
import useRebateConditionsForm from '../RebateConditionsForm/hooks/useRebateConditionsForm'
import { useContractDetailsContext } from 'features/ContractDetails/Providers/ContractDetailsContextProvider'
import { safeNumber } from 'helper/common'
import {
  getMarketShareThresholdFieldName,
  getRebateLabel
} from '../RebateConditionsForm/utils'
import {
  REBATE_CONDITIONS_TYPE,
  REBATE_CONDITIONS_TYPE_NAME
} from 'features/ContractDetails/RebateConditions/RebateConditionsForm/constants'
import {
  HOSPITAL_OPTIONS,
  PURCHASE_VOLUME_THRESHOLD_MEASURED_IN,
  THRESHOLD_MEASURED_IN_OPTIONS
} from '../RebateConditionsForm/MarketShareRebateCondition/constants'
import TiersView from '../RebateConditionsForm/MarketShareRebateCondition/TiersView/TiersView'
import TiersEmptyState from '../RebateConditionsForm/MarketShareRebateCondition/TiersEmptyState/TiersEmptyState'
import { LABELS } from 'constants/txt'
import './styles.scss'
import { ViewNoteButton } from 'features/ContractDetails/ViewNote/ViewNoteButton'
import { ViewNotePopup } from 'features/ContractDetails/ViewNote/ViewNotePopup'

const formatCurrency = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  currencyDisplay: 'narrowSymbol'
})

const formatPercent = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

const RebateConditionsPreview = () => {
  const contractDetailsContext = useContractDetailsContext()
  const { tiers } = useRebateConditionsForm()

  const { details } = contractDetailsContext.state
  const { updateRebateNoteAsync } = contractDetailsContext.actions
  const rebate = details.rebate
  const isMarketShare =
    details.rebate_type === REBATE_CONDITIONS_TYPE.MARKET_SHARE_REBATE
  const isTiered = isMarketShare && !!rebate?.is_tiered
  const isPurchaseVolume =
    rebate?.threshold_measured_in === PURCHASE_VOLUME_THRESHOLD_MEASURED_IN

  const canViewNote = !!details.can_view_rebate_note
  const canEditNote = !!details.can_manage_rebate_note
  const note = details.rebate_note ?? null

  const rebateTypeName = isMarketShare
    ? isTiered
      ? 'Tiered market share rebate'
      : 'Non-tiered market share rebate'
    : REBATE_CONDITIONS_TYPE_NAME[details.rebate_type]

  const thresholdMeasuredInLabel =
    THRESHOLD_MEASURED_IN_OPTIONS.find(
      (option) => option.value === rebate?.threshold_measured_in
    )?.label ?? THRESHOLD_MEASURED_IN_OPTIONS[0].label

  const applicabilityLabel = HOSPITAL_OPTIONS.find(
    (option) => option.value === rebate?.apply_to_all_hospitals
  )?.label

  const marketShareThresholdValue = () => {
    if (!rebate) return ''

    const thresholdField = getMarketShareThresholdFieldName(
      rebate.threshold_measured_in
    )
    const isVolumeThreshold = thresholdField === 'purchase_volume_threshold'

    return isVolumeThreshold
      ? formatCurrency.format(safeNumber(rebate.purchase_volume_threshold))
      : formatPercent.format(safeNumber(rebate.market_share_threshold) / 100)
  }

  return (
    <div className="rebate-conditions-preview column gap-24">
      <RowLayout title="Rebate type">
        <div className="column gap-24">
          <FieldWithLabel title={LABELS.REBATE_TYPE}>
            <Typography.Body1>{rebateTypeName}</Typography.Body1>
          </FieldWithLabel>
          <ViewNoteButton
            canViewNote={canViewNote}
            canEditNote={canEditNote}
            note={note}
            title="Rebate note"
          />
          <ViewNotePopup
            note={note}
            updateContractNote={updateRebateNoteAsync}
            title="Rebate note"
            subtitle="Notes about this rebate visible only for users from your Health System."
          />
        </div>
      </RowLayout>
      {details.rebate_type === REBATE_CONDITIONS_TYPE.STRAIGHT_REBATE &&
        rebate && (
          <>
            <Separator />
            <RowLayout title="Rebate conditions">
              <div className="rebate-conditions-preview__grid gap-24">
                <FieldWithLabel title={LABELS.REBATE_AMOUNT}>
                  {formatPercent.format(safeNumber(rebate.amount) / 100)}
                </FieldWithLabel>
                <FieldWithLabel title={LABELS.PERIOD_THRESHOLD}>
                  {getRebateLabel(rebate.period_threshold)}
                </FieldWithLabel>
              </div>
            </RowLayout>
          </>
        )}
      {details.rebate_type === REBATE_CONDITIONS_TYPE.GROWTH_REBATE && rebate && (
        <>
          <Separator />
          <RowLayout title="Rebate conditions">
            <div className="rebate-conditions-preview__grid gap-24">
              <FieldWithLabel title={LABELS.BASELINE_SPEND}>
                {formatCurrency.format(
                  safeNumber(rebate.purchase_volume_threshold)
                )}
              </FieldWithLabel>
              <FieldWithLabel title={LABELS.REBATE_AMOUNT}>
                {formatPercent.format(safeNumber(rebate.amount) / 100)}
              </FieldWithLabel>
              <FieldWithLabel title={LABELS.PERIOD_THRESHOLD}>
                {getRebateLabel(rebate.period_threshold)}
              </FieldWithLabel>
            </div>
          </RowLayout>
        </>
      )}
      {isMarketShare && rebate && (
        <>
          <Separator />
          <RowLayout title="Rebate conditions">
            <div className="rebate-conditions-preview__grid gap-24">
              <FieldWithLabel title={LABELS.THRESHOLD_MEASURED_IN}>
                {thresholdMeasuredInLabel}
              </FieldWithLabel>
              {!isTiered && (
                <>
                  <FieldWithLabel
                    title={
                      isPurchaseVolume
                        ? LABELS.THRESHOLD_DOLLARS
                        : LABELS.THRESHOLD
                    }
                  >
                    {marketShareThresholdValue()}
                  </FieldWithLabel>
                  <FieldWithLabel title={LABELS.REBATE_AMOUNT}>
                    {formatPercent.format(safeNumber(rebate.amount) / 100)}
                  </FieldWithLabel>
                </>
              )}
              <FieldWithLabel title={LABELS.PERIOD_THRESHOLD}>
                {getRebateLabel(rebate.period_threshold)}
              </FieldWithLabel>
            </div>
          </RowLayout>
          {isTiered && (
            <>
              <Separator />
              <div className="market-share-tiers">
                <RowLayout
                  title="Tiers"
                  subtitle="Base ladder, applies to every quarter of the contract term."
                >
                  {tiers.length ? (
                    <TiersView
                      tiers={tiers}
                      isPurchaseVolume={isPurchaseVolume}
                      canEditTiers={false}
                      onEdit={() => undefined}
                    />
                  ) : (
                    <TiersEmptyState
                      onAdd={() => undefined}
                      canAddTiers={false}
                    />
                  )}
                </RowLayout>
              </div>
            </>
          )}
          <Separator />
          <RowLayout title="Applicability">
            <FieldWithLabel title={LABELS.APPLY_TO_ALL_HOSPITALS}>
              <Typography.Body1>{applicabilityLabel}</Typography.Body1>
            </FieldWithLabel>
          </RowLayout>
        </>
      )}
    </div>
  )
}

export default RebateConditionsPreview
